import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { forgotPasswordSchema, type ForgotPasswordFormData } from '@/utils/validation';
import { romanianLabels } from '@/utils/romanianLabels';

interface ForgotPasswordFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onBackToLogin?: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onSuccess, onError, onBackToLogin }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [error, setError] = useState<string>('');
  
  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setIsLoading(true);
      setError('');
      
      const response = await fetch('/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send reset instructions');
      }

      setResetSent(true);
      onSuccess?.();
    } catch (error: any) {
      const errorMessage = error.message || romanianLabels.error.networkError;
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl text-center text-romanian">
          {romanianLabels.form.forgotPassword}
        </CardTitle>
        <CardDescription className="text-center">
          Introduceți adresa de email pentru a primi instrucțiuni de resetare a parolei
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {!resetSent ? (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* Email Field */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{romanianLabels.form.email}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          type="email"
                          placeholder="Introduceți adresa de email"
                          disabled={isLoading}
                          className="pl-10"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormDescription>
              Vă vor fi trimise instrucțiuni de resetare a parolei la această adresă.
            </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Error Message */}
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {romanianLabels.status.sending}
                  </>
                ) : (
                  romanianLabels.form.sendResetInstructions
                )}
              </Button>
            </form>
          </Form>
        ) : (
          <div className="text-center space-y-4">
            <div className="p-4 rounded-lg bg-green-50 border border-green-200">
              <Mail className="mx-auto h-12 w-12 text-green-600" />
              <h3 className="mt-4 text-lg font-medium text-green-900">
                {romanianLabels.form.resetInstructionsSent}
              </h3>
              <p className="mt-2 text-sm text-green-700">
                {romanianLabels.form.resetInstructionsSentDescription}
              </p>
            </div>
            
            <Button
              variant="outline"
              onClick={onBackToLogin}
              className="w-full"
            >
              {romanianLabels.form.backToLogin}
            </Button>
          </div>
        )}

        {/* Back to Login Link */}
        <div className="mt-4 text-center">
          <button
            type="button"
            className="text-sm text-primary hover:underline focus:outline-none"
            onClick={onBackToLogin}
          >
            ← Înapoi la autentificare
          </button>
        </div>
      </CardContent>
    </Card>
  );
};